import { apiSlice } from "../services/apiSlice";
import { transformFilters } from "../utils/transformFilters";

export const organizatiopApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getDynamicColumns: builder.query({
            query: (scope) => ({
                url: `/search-query/columns`,
                params: { scope },
            }),
        }),

        getDynamicFilters: builder.query({
            query: (scope) => ({
                url: `/search-query/filters`,
                params: { scope },
            }),
        }),
        updateOrganization: builder.mutation({
            query: (data) => ({
                url: `/organizations/${data?.id}`,
                method: "PUT",
                body: {
                    ...data,
                },
            }),
        }),

        presignedOrganizationProfile: builder.mutation({
            query: (data) => ({
                url: `/organizations/presigned-url`,
                method: "POST",
                body: {
                    ...data,
                },
            }),
        }),
        getOrganizations: builder.query({
            query: ({ page, perPage, sort, query, filters = {}, visibleColumns = [], importId }) => {

                const params: Record<string, any> = {
                    page,
                    perPage,
                    query,
                };

                const transformedFilters = transformFilters(filters);
                if (transformedFilters.conditions.length > 0) {
                    params.filters = encodeURIComponent(JSON.stringify(transformedFilters));
                }

                if (sort) {
                    params.sort = sort.field;
                    params.sortDir = sort.direction;
                }

                if (visibleColumns.length > 0) {
                    params.listingColumns = visibleColumns
                        .filter((col: any) => col !== 'organization_id')
                        .join(',');
                }

                if (importId) {
                    params.importJobId = importId
                }

                return {
                    url: '/organizations',
                    method: 'GET',
                    params,
                };
            },
        }),
        // getOrganizationsMinimal: builder.query({
        //     query: ({pae}) => ({
        //         url: `/organizations/minimal`,
        //     }),
        // }),
        getOrganizationsMinimal: builder.query<any, { page?: number; perPage?: number; query?: string }>({
            query: ({ page = 1, perPage = 50, query = "" }) => ({
                url: `/organizations/minimal`,
                params: { page, perPage, query },
            }),
        }),

        exportOrganizations: builder.query<any, {
            sort?: { field: string; direction: "ASC" | "DESD" };
            query?: string;
            filters?: Record<string, any>;
            visibleColumns?: string[];
        }>({
            query: ({ sort, query, filters = {}, visibleColumns = [] }) => {
                const params: Record<string, any> = {
                    query,
                    ...filters,
                    listingColumns: visibleColumns.length > 0 ? visibleColumns : undefined,
                    export: 1,
                };

                if (sort) {
                    params.sortField = sort.field;
                    params.sortDirection = sort.direction;
                }

                const queryString = buildQueryParams(params);

                return {
                    url: `organizations?${queryString}`,
                    method: 'GET',
                    // Add these response handler options
                    responseHandler: (response) => response.blob(),
                    cache: 'no-store',
                };
            },
        }),
        deleteOrganization: builder.mutation<any, number>({
            query: (id) => ({
                url: `organizations/${id}`,
                method: 'DELETE',
            }),
        }),
        getMultipleOrgRecords: builder.query({
            query: (recordIds) => ({
                url: `/organizations/multiple?recordIds=${encodeURIComponent(recordIds)}`,
                method: 'GET',
            }),
        }),

        getMultipleOrgRecordsById: builder.mutation({
            query: (recordIds) => ({
                url: `/organizations/multiple?recordIds=${encodeURIComponent(recordIds)}`,
                method: 'GET',
            }),
        }),

        getOrganizationJoinRequests: builder.query({
            query: (arg) => {
                const payload = (typeof arg === 'object' && arg !== null) ? arg : { organizationId: arg };
                const rawOrganizationId = payload.organizationId;

                const normalizedOrganizationId =
                    typeof rawOrganizationId === 'object' && rawOrganizationId !== null
                        ? rawOrganizationId.id ?? rawOrganizationId.organizationId
                        : rawOrganizationId;

                const page = payload.page ?? 1;
                const perPage = payload.perPage ?? payload.limit ?? 10;

                return {
                    url: `/organizations/${Number(normalizedOrganizationId)}/join-requests`,
                    method: 'GET',
                    params: { page, limit: perPage },
                };
            },
        }),

        approveOrganizationJoinRequest: builder.mutation({
            query: ({ organizationId, requestId }) => ({
                url: `/organizations/${organizationId}/join-requests/${requestId}/approve`,
                method: 'PUT',
            }),
        }),

        rejectOrganizationJoinRequest: builder.mutation({
            query: ({ organizationId, requestId }) => ({
                url: `/organizations/${organizationId}/join-requests/${requestId}/reject`,
                method: 'PUT',
            }),
        }),
        
        mergeOrganizations: builder.mutation({
            query: (data) => ({
                url: `/organizations/${data.id}/merge`,
                method: "PUT",
                body: {
                    ...data,
                },
            }),
        }),
    }),
});

export const {
    useGetDynamicColumnsQuery,
    useGetDynamicFiltersQuery,
    useUpdateOrganizationMutation,
    useLazyGetOrganizationsQuery,
    useGetOrganizationsMinimalQuery,
    useLazyExportOrganizationsQuery,
    useDeleteOrganizationMutation,
    useGetMultipleOrgRecordsQuery,
    useMergeOrganizationsMutation,
    usePresignedOrganizationProfileMutation,
    useGetMultipleOrgRecordsByIdMutation,
    useLazyGetOrganizationsMinimalQuery,
    useGetOrganizationJoinRequestsQuery,
    useApproveOrganizationJoinRequestMutation,
    useRejectOrganizationJoinRequestMutation
} = organizatiopApiSlice;

function buildQueryParams(params: Record<string, any>): string {
    const query = new URLSearchParams();

    for (const key in params) {
        const value = params[key];

        if (
            value === undefined ||
            value === null ||
            (typeof value === "string" && value.trim() === "") ||
            (Array.isArray(value) && value.length === 0)
        ) {
            continue;
        }

        if (Array.isArray(value)) {
            query.append(key, value.join(","));
        } else {
            query.append(key, value.toString());
        }
    }

    return query.toString();
}